import { useCallback, useState } from 'react';

import { Divider, IconButton } from '@mui/material';

import { MoreVert } from '@mui/icons-material';

import { observer } from 'mobx-react';

import { EditorContent } from '@tiptap/react';

import { Button } from 'components/Button';

import { formatMoment } from 'utils/formatters/timezone';

import { MenuItem, StyledPopover } from 'containers/TaskDetails/styles';

import { useStore } from 'utils/hooks/store';

import { useCommentEditor } from './comment-mention/useCommentEditor';
import { parseCommentContent, commentContentToText } from './comment-mention/content';
import CommentContentView from './comment-mention/CommentContentView';
import {
  CommentActions,
  CommentAuthor,
  CommentMeta,
  CommentPrimary,
  CommentContent,
  CommentEditorSurface,
  CommentEditActions,
} from './styles';

type CommentCardProps = {
  user: {
    name: string;
    uuid: string;
  };
  createdAt: string;
  content: string;
  onEdit: (value: string) => void;
  onDelete: () => void;
};

const CommentCard: React.FC<CommentCardProps> = observer(({ user, createdAt, content, onEdit, onDelete }) => {
  const { session } = useStore();
  const [isComposing, setIsComposing] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const mentionsEnabled = session.user?.licenses?.documents ?? false;

  const editor = useCommentEditor({ mentionsEnabled, initialContent: content });

  const handleEvent = useCallback(
    (callback: () => void) => (e: React.MouseEvent | React.FormEvent) => {
      e.preventDefault();
      e.stopPropagation();
      callback();
    },
    []
  );

  const resetState = useCallback(() => {
    setIsComposing(false);
    setAnchorEl(null);
  }, []);

  const handleSubmit = useCallback(
    handleEvent(() => {
      if (!editor || editor.isEmpty) {
        return;
      }
      const value = mentionsEnabled ? JSON.stringify(editor.getJSON()) : editor.getText();
      if (commentContentToText(value) === commentContentToText(content)) {
        resetState();
        return;
      }
      resetState();
      onEdit(value);
    }),
    [editor, mentionsEnabled, content, onEdit, resetState, handleEvent]
  );

  const handleOpenPopover = (e: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(e.currentTarget);
  };

  const handleEditClick = useCallback(
    handleEvent(() => {
      editor?.commands.setContent(content ? parseCommentContent(content) : '');
      setIsComposing(true);
    }),
    [editor, content, handleEvent]
  );

  const handleDeleteClick = useCallback(
    handleEvent(() => {
      onDelete();
    }),
    [onDelete, handleEvent]
  );

  const handleCancelClick = useCallback(
    handleEvent(() => {
      resetState();
    }),
    [resetState, handleEvent]
  );

  const isCurrentUser = session?.user?.uuid === user.uuid;
  const canEdit = isCurrentUser && !isComposing;

  return (
    <>
      <CommentPrimary>
        <CommentAuthor>{user.name || 'Unknown'}</CommentAuthor>

        {canEdit && (
          <>
            <CommentActions>
              <IconButton sx={{ padding: 0 }} onClick={handleOpenPopover}>
                <MoreVert />
              </IconButton>
            </CommentActions>

            <StyledPopover
              disableScrollLock
              id="comment-actions"
              data-testid="comment-actions"
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              {isCurrentUser && (
                <MenuItem id="edit-comment-button" onClick={handleEditClick}>
                  Edit
                </MenuItem>
              )}
              <Divider sx={{ m: 0 }} />
              <MenuItem id="delete-comment-button" onClick={handleDeleteClick}>
                Delete
              </MenuItem>
            </StyledPopover>
          </>
        )}
      </CommentPrimary>

      <CommentMeta data-testid="document-comment-time">{formatMoment(createdAt)}</CommentMeta>

      {isComposing ? (
        <form onSubmit={handleSubmit}>
          <CommentEditorSurface>
            <EditorContent editor={editor} />
          </CommentEditorSurface>
          <CommentEditActions>
            <Button type="button" variant="outlined" onClick={handleCancelClick}>
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              Accept
            </Button>
          </CommentEditActions>
        </form>
      ) : (
        <CommentContent>
          <CommentContentView content={content} mentionsEnabled={mentionsEnabled} />
        </CommentContent>
      )}
    </>
  );
});

export default CommentCard;
